import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/modules/shared/contexts/AuthContext';
import {
  programAccessService,
  PROGRAM_ACTION_KEYS,
  PROGRAM_FEATURE_KEYS,
  type MyProgramAccessDto,
} from '../services/programAccessService';
import { programService } from '../services/programService';
import type { UserProgramAssignmentDto } from '@/modules/shared/types';

interface UseProgramAccessOptions {
  programId: string | undefined;
  enabled?: boolean;
}

type UseProgramAccessInput = string | undefined | UseProgramAccessOptions;

interface ProgramAccessResult {
  access: MyProgramAccessDto | null;
  isLoading: boolean;
  error: string | null;
  hasFeature: (featureKey: string) => boolean;
  hasAction: (actionKey: string) => boolean;
  features: Record<string, MyProgramAccessDto['features'][string]>;
  actions: Record<string, MyProgramAccessDto['actions'][string]>;
  hasAccess: boolean;
  programs: UserProgramAssignmentDto[];
  refetch: () => void;
}

const SUPERADMIN_FEATURES = Object.values(PROGRAM_FEATURE_KEYS).reduce<Record<string, MyProgramAccessDto['features'][string]>>(
  (accumulator, featureKey) => {
    accumulator[featureKey] = {
      key: featureKey,
      menuKey: featureKey,
      programEnabled: true,
      roleAllowed: true,
      effectiveAccess: true,
      source: 'superadmin-bypass',
    };
    return accumulator;
  },
  {}
);

const SUPERADMIN_ACTIONS = Object.values(PROGRAM_ACTION_KEYS).reduce<Record<string, MyProgramAccessDto['actions'][string]>>(
  (accumulator, actionKey) => {
    accumulator[actionKey] = {
      key: actionKey,
      featureKey: '',
      programEnabled: true,
      roleAllowed: true,
      effectiveAccess: true,
      source: 'superadmin-bypass',
    };
    return accumulator;
  },
  {}
);

function normalizeOptions(input: UseProgramAccessInput): UseProgramAccessOptions {
  return typeof input === 'string' || input === undefined
    ? { programId: input, enabled: true }
    : input;
}

export function useProgramAccess(input: UseProgramAccessInput): ProgramAccessResult {
  const { programId, enabled = true } = normalizeOptions(input);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'ProductAdmin';
  const useProjectedAccess = typeof input === 'object' && input !== null;

  const {
    data: response,
    isLoading,
    error,
    refetch: refetchProjected,
  } = useQuery({
    queryKey: ['programAccess', programId, user?.userId],
    queryFn: () => programAccessService.getMyProgramAccess(programId!),
    enabled: useProjectedAccess && enabled && !!programId && !!user && !isSuperAdmin,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const {
    data: assignments,
    isLoading: assignmentsLoading,
    refetch: refetchAssignments,
  } = useQuery({
    queryKey: ['myPrograms', user?.userId],
    queryFn: () => programService.getMyPrograms(),
    enabled: !!user && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
  });

  const access = response?.success ? response.data : null;
  const errorMessage = isSuperAdmin
    ? null
    : response?.success === false
      ? response.message || 'Failed to load program access.'
      : error instanceof Error
        ? error.message
        : error
          ? 'Failed to load program access.'
          : null;

  const features = useMemo(() => {
    if (isSuperAdmin) {
      return SUPERADMIN_FEATURES;
    }

    return access?.features ?? {};
  }, [access?.features, isSuperAdmin]);

  const actions = useMemo(() => {
    if (isSuperAdmin) {
      return SUPERADMIN_ACTIONS;
    }

    return access?.actions ?? {};
  }, [access?.actions, isSuperAdmin]);

  const hasFeature = (featureKey: string): boolean => {
    if (isSuperAdmin) return true;
    return features[featureKey]?.effectiveAccess ?? false;
  };

  const hasAction = (actionKey: string): boolean => {
    if (isSuperAdmin) return true;
    return actions[actionKey]?.effectiveAccess ?? false;
  };

  const programs = useMemo(() => {
    if (isSuperAdmin) {
      return [];
    }

    return (assignments?.data ?? []).filter((program) => program.status === 'ACTIVE');
  }, [assignments?.data, isSuperAdmin]);

  const selectedProgramId = typeof window !== 'undefined'
    ? sessionStorage.getItem('epprem_selected_program')
    : null;
  const isAssignedProgram = !!programId && programs.some((program) => program.programId === programId);
  const matchesSelection = !selectedProgramId || selectedProgramId === programId;
  const hasAccess = !isSuperAdmin && !!programId && isAssignedProgram && matchesSelection;

  const refetch = () => {
    void refetchAssignments();
    if (useProjectedAccess) {
      void refetchProjected();
    }
  };

  return {
    access,
    isLoading: !isSuperAdmin && (assignmentsLoading || (useProjectedAccess && isLoading)),
    error: errorMessage,
    hasFeature,
    hasAction,
    features,
    actions,
    hasAccess,
    programs,
    refetch,
  };
}
